import { Injectable, inject } from '@angular/core';
import { calculateDistance } from '@functions';
import {
  DetectionService,
  LogicService,
  MessageService,
  RenderService,
  SimulationService,
} from '@services';
import '@tensorflow/tfjs-backend-webgl';
import { IntracorporealKnotMetricsService } from './intracorporeal-knot-metrics.service';

const KNOT_TO_TISSUEWIDTH_RATIO = 0.9;
const CONFIDENCE_CLOSEDKNOT = 0.3;
const COEFFICIENT_DISTANCE_TOOL_TO_TISSUE = 2;

@Injectable({
  providedIn: 'root',
})
export class SharedService {
  private detectionService = inject(DetectionService);
  private intracorporealKnotMetricsService = inject(
    IntracorporealKnotMetricsService,
  );
  private logicService = inject(LogicService);
  private messageService = inject(MessageService);
  private renderService = inject(RenderService);
  private simulationService = inject(SimulationService);

  cycleWorkerId: string = undefined;
  piercingWorkerId: string = undefined;
  piercingClassWorkerId: string = undefined;

  isInTissue: boolean;

  checkSample(): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      try {
        const tissue = this.detectionService.getBox(
          'tissue',
          this.cycleWorkerId,
        );

        const tissueWidth = tissue?.max.x - tissue?.min.x;
        const tissueHeigth = tissue?.max.y - tissue?.min.y;
        const isOrientationVertical = tissueWidth < 0.7 * tissueHeigth;

        this.logicService.checkSamplePosition({
          sample: 'tissue',
          workerId: this.cycleWorkerId,
          confidence: 0.5,
          width: 640,
          height: 460,
          moveX: -110,
          moveY: 60,
          image: 'overlays/suture-block.svg',
          additionalCondition: isOrientationVertical,
        });
        resolve(this.simulationService.state() === 'simulation');
      } catch (error) {
        reject(error);
      }
    });
  }

  checkInstruments(): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      try {
        this.logicService
          .checkInstrumentsPosition([
            {
              id: 'toolLeft',
              workerId: this.cycleWorkerId,
              confidence: 0.2,
              moveX: -350,
              radius: 90,
              image: 'overlays/instrument-left.svg',
            },
            {
              id: 'toolRight',
              workerId: this.cycleWorkerId,
              confidence: 0.2,
              moveX: 170,
              radius: 90,
              image: 'overlays/instrument-right.svg',
            },
          ])
          .then((instrumentsConfirmed) => {
            if (!instrumentsConfirmed) {
              this.messageService.showDetectionMessage({
                text: 'Bring your instruments together in the camera view',
                identifier: 'checkInstruments',
              });
            }
            resolve(instrumentsConfirmed);
          });
      } catch (error) {
        reject(error);
      }
    });
  }

  piercePenrose(): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      try {
        const penrose = this.detectionService.getBox(
          'tissue',
          this.cycleWorkerId,
        );

        const piercingInside = this.detectionService.getBox(
          'needleInside',
          this.piercingWorkerId,
        );

        this.messageService.showDetectionMessage({
          text: 'Pass the needle through the dots',
          identifier: 'piercePenrose',
        });

        if (penrose) {
          this.renderService.addDynamic({
            overlay: {
              type: 'image',
              image: 'overlays/pierce-penrose.svg',
              x: penrose.max.x + 100,
              y: penrose.min.y - 53,
            },
          });
        }
        this.isInTissue = piercingInside
          ? piercingInside?.center.x <
            penrose?.min.x + (penrose?.max.x - penrose?.min.x) / 2
          : this.isInTissue;

        const isPierced =
          this.detectionService.getRegressionTrend(
            'isPierced',
            this.piercingClassWorkerId,
            3,
          ) &&
          !this.detectionService.getRegressionTrend(
            'isNotPierced',
            this.piercingClassWorkerId,
            3,
            0.9,
          ) &&
          this.isInTissue;

        resolve(isPierced);
      } catch (error) {
        reject(error);
      }
    });
  }

  pullSuture(): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      try {
        const penrose = this.detectionService.getBox(
          'tissue',
          this.cycleWorkerId,
        );

        if (penrose) {
          this.renderService.addDynamic({
            overlay: {
              type: 'image',
              image: 'overlays/pull-suture.svg',
              x: penrose.min.x - 200,
              y: penrose.center.y,
            },
          });
        }

        const isSuturePulled = this.detectionService.getRegressionTrend(
          'isPulling',
          this.piercingClassWorkerId,
          2,
        );

        resolve(isSuturePulled);
      } catch (error) {
        reject(error);
      }
    });
  }

  tieKnots(): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      try {
        const isKnotTied =
          this.detectClosedKnotExistence() &&
          this.areToolsDistantFromTissue() &&
          this.isClosedKnotSmall();

        if (isKnotTied) {
          this.messageService.showDetectionMessage({
            text: 'Knot tying detected! Keep up the good work by tying any remaining knots',
            identifier: 'knotDetection',
          });
        }
        resolve(isKnotTied);
      } catch (error) {
        reject(error);
      }
    });
  }

  cutThreads(): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      try {
        const isThreadCut = false;
        resolve(isThreadCut);
      } catch (error) {
        reject(error);
      }
    });
  }

  isClosedKnotSmall() {
    const [tissue, closedKnot] = this.detectionService.getBoxes(
      ['tissue', 'closedKnot'],
      this.cycleWorkerId,
    );
    const tissueWidth = tissue ? tissue.max.x - tissue.min.x : null;
    const knotWidth = closedKnot ? closedKnot.max.x - closedKnot.min.x : null;
    const isKnotSmall = knotWidth < KNOT_TO_TISSUEWIDTH_RATIO * tissueWidth;
    return isKnotSmall;
  }

  areToolsDistantFromTissue() {
    const [toolLeft, toolRight, tissue] = this.detectionService.getBoxes(
      ['toolLeft', 'toolRight', 'tissue'],
      this.cycleWorkerId,
    );

    const tissueWidth = tissue ? tissue.max.x - tissue.min.x : null;
    const distancetoolLeftTissueCenter =
      tissue && toolLeft
        ? calculateDistance(
            { x: toolLeft.center.x, y: toolLeft.center.y },
            { x: tissue.center.x, y: tissue.center.y },
          )
        : null;
    const distancetoolRightTissueCenter =
      tissue && toolRight
        ? calculateDistance(
            { x: toolRight.center.x, y: toolRight.center.y },
            { x: tissue.center.x, y: tissue.center.y },
          )
        : null;
    const isToolsDistantFromTissue =
      distancetoolLeftTissueCenter + distancetoolRightTissueCenter >
      COEFFICIENT_DISTANCE_TOOL_TO_TISSUE * tissueWidth;
    return isToolsDistantFromTissue;
  }

  detectClosedKnotExistence() {
    const closedKnot = this.detectionService.getBox(
      'closedKnot',
      this.cycleWorkerId,
    );

    const numberOfOccurencesOfObjects =
      this.detectionService.getBoxesHistorySize(this.cycleWorkerId);

    const countOpenKnotAbsence = this.detectionService.getNumberOfBoxEmptys(
      'openKnot',
      this.cycleWorkerId,
    );

    const countClosedKnotAbsence = this.detectionService.getNumberOfBoxEmptys(
      'closedKnot',
      this.cycleWorkerId,
    );

    const isCLosedKnotFound =
      closedKnot &&
      closedKnot.confidence > CONFIDENCE_CLOSEDKNOT &&
      countClosedKnotAbsence < 0.5 * numberOfOccurencesOfObjects;

    return isCLosedKnotFound;
  }

  toolTracking() {
    this.intracorporealKnotMetricsService.trackLeftTool(
      this.detectionService.getBox('toolLeft', this.cycleWorkerId),
    );
    this.intracorporealKnotMetricsService.trackRightTool(
      this.detectionService.getBox('toolRight', this.cycleWorkerId),
    );
  }
}
